<script setup lang="ts">
/**
 * 公開作品への通報（#940）。作品情報のダイアログの「この作品を報告する」から開く。
 *
 * - サインインしていなくても送れる（権利者は C&M のアカウントを持っていないことが多い）。そのときは
 *   返事のためのメールアドレスが必須。サインインしていればアカウントのメールアドレスに返すので欄を出さない
 * - 送ると、打ち込んだ内容の控えがメールで届く（`subscribers/work_report_notifications.py`）
 * - 対応は人がする（自動で非公開にはしない）
 */
const dialog = defineModel<boolean>()
const props = defineProps<{ slug: string, title?: string }>()

const { reportWork } = useApi()
const { isAuthenticated } = useAuthInfo()

type Reason = 'rights' | 'inappropriate' | 'other'
const reasons: { value: Reason, label: string }[] = [
  { value: 'rights', label: '権利の侵害（自分の絵・音・コードなどが使われている）' },
  { value: 'inappropriate', label: '不適切な内容' },
  { value: 'other', label: 'その他' },
]

const reason = ref<Reason>('rights')
const message = ref('')
const email = ref('')
const sending = ref(false)
const sentTo = ref<string | null>(null)
const error = ref<string | null>(null)

const EMAIL = /^[^@\s]+@[^@\s]+\.[^@\s]+$/
const canSend = computed(() =>
  message.value.trim().length > 0
  && (isAuthenticated || EMAIL.test(email.value.trim()))
  && !sending.value)

watch(dialog, (open) => {
  if (!open) return
  reason.value = 'rights'
  message.value = ''
  email.value = ''
  sentTo.value = null
  error.value = null
})

const send = async () => {
  if (!canSend.value) return
  sending.value = true
  error.value = null
  try {
    const res = await reportWork(props.slug, {
      reason: reason.value,
      message: message.value.trim(),
      ...(isAuthenticated ? {} : { email: email.value.trim() }),
    }, isAuthenticated)
    sentTo.value = res.reporterEmail
  }
  catch (e) {
    const status = (e as { statusCode?: number, status?: number })?.statusCode
      ?? (e as { status?: number })?.status
    error.value = status === 429
      ? 'この作品は、今日はもう報告を受け付けています。'
      : '送れませんでした。時間をおいてもう一度お試しください。'
  }
  finally {
    sending.value = false
  }
}
</script>

<template>
  <DialogMobileDialog
    v-model="dialog"
    title="この作品を報告する"
    :close-text="sentTo ? '閉じる' : 'やめておく'"
  >
    <div
      v-if="sentTo"
      class="report"
      data-testid="work-report-done"
    >
      <p>受け付けました。内容を確認し、必要に応じてご連絡します。</p>
      <p class="report__note">
        {{ sentTo }} に控えを送りました。
      </p>
    </div>
    <div
      v-else
      class="report"
      data-testid="work-report-form"
    >
      <p
        v-if="props.title"
        class="report__work"
      >
        {{ props.title }}
      </p>
      <v-radio-group
        v-model="reason"
        hide-details
        density="compact"
      >
        <v-radio
          v-for="r in reasons"
          :key="r.value"
          :value="r.value"
          :label="r.label"
        />
      </v-radio-group>
      <v-textarea
        v-model="message"
        label="くわしく"
        rows="4"
        auto-grow
        counter="2000"
        maxlength="2000"
        variant="outlined"
        density="comfortable"
        class="mt-3"
        data-testid="work-report-message"
      />
      <v-text-field
        v-if="!isAuthenticated"
        v-model="email"
        label="返事を送るメールアドレス"
        type="email"
        autocomplete="email"
        variant="outlined"
        density="comfortable"
        data-testid="work-report-email"
      />
      <p
        v-if="error"
        class="report__error"
      >
        {{ error }}
      </p>
    </div>
    <template
      v-if="!sentTo"
      #actions
    >
      <v-btn
        variant="flat"
        color="primary"
        :disabled="!canSend"
        :loading="sending"
        data-testid="work-report-send"
        @click="send"
      >
        送る
      </v-btn>
    </template>
  </DialogMobileDialog>
</template>

<style scoped>
.report__work {
  font-weight: bold;
  margin-bottom: 4px;
}

.report__note {
  margin-top: 8px;
  font-size: 0.9em;
  opacity: 0.8;
}

.report__error {
  color: #b3261e;
  font-size: 0.9em;
}
</style>
